import React, { useState } from "react";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";
import {
  Search,
  Clock,
  User,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import ReactPaginate from "react-paginate";

const SalaryHistory = () => {
  const [employeeId, setEmployeeId] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });
  const [viewMode, setViewMode] = useState("timeline"); // timeline or table

  const fetchHistory = async (page = 1) => {
    if (!employeeId.trim()) {
      setNotification({ type: "warning", message: "Please enter employee ID" });
      return;
    }
    setLoading(true);
    try {
      const response = await payrollService.getSalaryHistory(employeeId, {
        page,
        per_page: 10,
      });
      if (response.message) {
        setHistory(response.data.data || []);
        setPagination({
          current_page: response.data.current_page || 1,
          last_page: response.data.last_page || 1,
          total: response.data.total || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching salary history:", error);
      setNotification({
        type: "error",
        message: "Failed to fetch salary history",
      });
      setHistory([]);
      setPagination({ current_page: 1, last_page: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = ({ selected }) => {
    fetchHistory(selected + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Present";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const calculateChange = (current, previous) => {
    if (!previous) return { amount: 0, percentage: 0, type: "new" };
    const amount = current - previous;
    const percentage = ((amount / previous) * 100).toFixed(1);
    return {
      amount: Math.abs(amount),
      percentage: Math.abs(percentage),
      type: amount >= 0 ? "increase" : "decrease",
    };
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Salary History
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          View employee salary history
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && fetchHistory()}
              placeholder="Enter Employee ID..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Salary History for Employee: {employeeId}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("timeline")}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  viewMode === "timeline"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                <Clock size={16} className="inline mr-1" />
                Timeline
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  viewMode === "table"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                <DollarSign size={16} className="inline mr-1" />
                Table
              </button>
            </div>
          </div>

          {viewMode === "timeline" ? (
            <div className="space-y-6">
              {history.map((item, index) => {
                const previousItem = history[index + 1];
                const basicChange = calculateChange(
                  item.basic_salary,
                  previousItem?.basic_salary
                );
                const ctcChange = calculateChange(item.ctc, previousItem?.ctc);

                return (
                  <div key={item.id} className="relative">
                    {/* Timeline line */}
                    {index < history.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-300 dark:bg-gray-600"></div>
                    )}

                    <div className="flex items-start space-x-4">
                      {/* Timeline dot */}
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          item.is_active
                            ? "bg-green-100 dark:bg-green-900"
                            : "bg-gray-100 dark:bg-gray-700"
                        }`}
                      >
                        <DollarSign
                          size={20}
                          className={
                            item.is_active
                              ? "text-green-600 dark:text-green-400"
                              : "text-gray-600 dark:text-gray-400"
                          }
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                              Basic: ₹
                              {parseFloat(item.basic_salary).toLocaleString(
                                "en-IN"
                              )}{" "}
                              | CTC: ₹
                              {parseFloat(item.ctc).toLocaleString("en-IN")}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {formatDate(item.effective_from)} -{" "}
                              {formatDate(item.effective_to)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {basicChange.type !== "new" && (
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  basicChange.type === "increase"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                }`}
                              >
                                {basicChange.type === "increase" ? (
                                  <TrendingUp size={12} />
                                ) : (
                                  <TrendingDown size={12} />
                                )}
                                <span className="ml-1">
                                  {basicChange.type === "increase" ? "+" : "-"}₹
                                  {basicChange.amount.toLocaleString("en-IN")} (
                                  {basicChange.percentage}%)
                                </span>
                              </span>
                            )}
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                item.is_active
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300"
                              }`}
                            >
                              {item.is_active ? "Current" : "Previous"}
                            </span>
                          </div>
                        </div>

                        {/* Components breakdown */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                              Earnings
                            </h5>
                            {item.earnings_components?.map((comp, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between py-1"
                              >
                                <span className="text-gray-600 dark:text-gray-400">
                                  {comp.name}
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {parseFloat(comp.amount).toLocaleString(
                                    "en-IN"
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                              Deductions
                            </h5>
                            {item.deduction_components?.map((comp, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between py-1"
                              >
                                <span className="text-gray-600 dark:text-gray-400">
                                  {comp.name}
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {parseFloat(comp.amount).toLocaleString(
                                    "en-IN"
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                              Details
                            </h5>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Gross Salary:
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {parseFloat(item.gross_salary).toLocaleString(
                                    "en-IN"
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Created by:
                                </span>
                                <span className="font-medium">
                                  {item.creator?.name || "System"}
                                </span>
                              </div>
                              {item.updater && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Updated by:
                                  </span>
                                  <span className="font-medium">
                                    {item.updater.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Basic Salary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Gross Salary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      CTC
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Created By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {history.map((item, index) => {
                    const previousItem = history[index + 1];
                    const basicChange = calculateChange(
                      item.basic_salary,
                      previousItem?.basic_salary
                    );

                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div>
                            <div className="font-medium">
                              {formatDate(item.effective_from)} -{" "}
                              {formatDate(item.effective_to)}
                            </div>
                            {basicChange.type !== "new" && (
                              <div
                                className={`text-xs ${
                                  basicChange.type === "increase"
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {basicChange.type === "increase" ? "↗" : "↘"}{" "}
                                {basicChange.percentage}%
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ₹
                          {parseFloat(item.basic_salary).toLocaleString(
                            "en-IN"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ₹
                          {parseFloat(item.gross_salary).toLocaleString(
                            "en-IN"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ₹{parseFloat(item.ctc).toLocaleString("en-IN")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              item.is_active
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300"
                            }`}
                          >
                            {item.is_active ? "Current" : "Previous"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {item.creator?.name || "System"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="mt-6 flex justify-center">
              <ReactPaginate
                previousLabel="Previous"
                nextLabel="Next"
                breakLabel="..."
                pageCount={pagination.last_page}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageChange}
                containerClassName="flex items-center justify-center gap-2"
                pageClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                pageLinkClassName="text-gray-700 dark:text-gray-300"
                previousClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                previousLinkClassName="text-gray-700 dark:text-gray-300"
                nextClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                nextLinkClassName="text-gray-700 dark:text-gray-300"
                activeClassName="bg-blue-600 text-white hover:bg-blue-700"
                disabledClassName="opacity-50 cursor-not-allowed"
              />
            </div>
          )}
        </div>
      )}

      {history.length === 0 && !loading && employeeId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <User size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Salary History Found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No salary history records found for employee {employeeId}.
          </p>
        </div>
      )}
    </div>
  );
};

export default SalaryHistory;
